import { c as _c } from "react/compiler-runtime";
import React, { useState, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { StringUtils } from 'record-it-utils/utils/StringUtils/StringUtils';
import { ButtonStandard, ButtonStandardType } from '../../button/ButtonStandard/ButtonStandard';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useModalLifecycleEffect } from '../../hooks/useModalLifecycleEffect/useModalLifecycleEffect';
import { ConfirmModal, ConfirmModalProps, ConfirmModalWidthType } from '../../modal/ConfirmModal/ConfirmModal';
import { ConfirmModalBody } from '../../modal/ConfirmModalBody/ConfirmModalBody';
import { ConfirmModalButtonFooter, ConfirmModalButtonFooterButtonAlignment } from '../../modal/ConfirmModalButtonFooter/ConfirmModalButtonFooter';
import { ConfirmModalHeader } from '../../modal/ConfirmModalHeader/ConfirmModalHeader';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { ClockPicker, ClockPickerProps } from '../ClockPicker/ClockPicker';
import styles from './TimeClockPickerModal.module.css';
export type TimeClockPickerModalProps = Omit<PropsWithoutChildren<ConfirmModalProps>, 'widthType'> & {
  hours: number | null;
  minutes: number | null;
  onClose: () => void;
  onValueChanged: (hours: number, minutes: number) => void;
};
export const TimeClockPickerModal: FunctionComponent<TimeClockPickerModalProps> = React.memo(props => {
  const $ = _c(77);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let hours;
  let minutes;
  let onClose;
  let onValueChanged;
  let opened;
  let remainingProps;
  if ($[2] !== t0) {
    ({
      hours,
      minutes,
      opened,
      onClose,
      onValueChanged,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = hours;
    $[4] = minutes;
    $[5] = onClose;
    $[6] = onValueChanged;
    $[7] = opened;
    $[8] = remainingProps;
  } else {
    hours = $[3];
    minutes = $[4];
    onClose = $[5];
    onValueChanged = $[6];
    opened = $[7];
    remainingProps = $[8];
  }
  const [internalHours, setInternalHours] = useState(hours);
  const [internalMinutes, setInternalMinutes] = useState(minutes);
  const [mode, setMode] = useState("hours");
  const {
    t
  } = useTranslation("record-it-components");
  let t1;
  if ($[9] !== hours || $[10] !== minutes || $[11] !== onValueChanged) {
    t1 = (newHours, newMinutes) => {
      const newHoursWithFallback = newHours ?? 0;
      const newMinutesWithFallback = newMinutes ?? 0;
      if (newHoursWithFallback !== hours || newMinutesWithFallback !== minutes) {
        onValueChanged(newHoursWithFallback, newMinutesWithFallback);
      }
    };
    $[9] = hours;
    $[10] = minutes;
    $[11] = onValueChanged;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const callOnValueChanged = useEventCallback(t1);
  let t2;
  if ($[13] !== hours || $[14] !== minutes) {
    t2 = () => {
      setMode("hours");
      setInternalHours(hours);
      setInternalMinutes(minutes);
    };
    $[13] = hours;
    $[14] = minutes;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const reset = useEventCallback(t2);
  let t3;
  if ($[16] !== opened || $[17] !== reset) {
    t3 = {
      opened,
      afterOpened: reset
    };
    $[16] = opened;
    $[17] = reset;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  useModalLifecycleEffect(t3);
  let t4;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = newHours_0 => {
      setInternalHours(newHours_0);
      setMode("minutes");
    };
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const handleHoursClockPickerSelected = useEventCallback(t4);
  let t5;
  if ($[20] !== callOnValueChanged || $[21] !== internalHours || $[22] !== onClose) {
    t5 = newMinutes_0 => {
      setInternalMinutes(newMinutes_0);
      callOnValueChanged(internalHours, newMinutes_0);
      onClose();
    };
    $[20] = callOnValueChanged;
    $[21] = internalHours;
    $[22] = onClose;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  const handleMinutesClockPickerSelected = useEventCallback(t5);
  let t6;
  if ($[24] !== onClose || $[25] !== reset) {
    t6 = () => {
      reset();
      onClose();
    };
    $[24] = onClose;
    $[25] = reset;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const handleCancelClick = useEventCallback(t6);
  let t7;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setMode("minutes");
    };
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const handleNextClick = useEventCallback(t7);
  let t8;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setMode("hours");
    };
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const handlePreviousClick = useEventCallback(t8);
  let t9;
  if ($[29] !== callOnValueChanged || $[30] !== internalHours || $[31] !== internalMinutes || $[32] !== onClose) {
    t9 = () => {
      callOnValueChanged(internalHours, internalMinutes);
      onClose();
    };
    $[29] = callOnValueChanged;
    $[30] = internalHours;
    $[31] = internalMinutes;
    $[32] = onClose;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const handleFinishClick = useEventCallback(t9);
  const t10 = mode === "hours" ? "true" : "false";
  let t11;
  if ($[34] !== internalHours) {
    t11 = StringUtils.padLeft(internalHours?.toString() ?? "--", 2);
    $[34] = internalHours;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t10 || $[37] !== t11) {
    t12 = <span className={styles.timePreviewPart} data-active={t10} data-testid="TimeClockPickerModal.TimePreviewHours">{t11}</span>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const t13 = mode === "minutes" ? "true" : "false";
  let t14;
  if ($[39] !== internalMinutes) {
    t14 = StringUtils.padLeft(internalMinutes?.toString() ?? "--", 2);
    $[39] = internalMinutes;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t13 || $[42] !== t14) {
    t15 = <span className={styles.timePreviewPart} data-active={t13} data-testid="TimeClockPickerModal.TimePreviewMinutes">{t14}</span>;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== t12 || $[45] !== t15) {
    t16 = <ConfirmModalHeader><span className={styles.timePreview} data-testid="TimeClockPickerModal.TimePreview">{t12}:{t15}</span></ConfirmModalHeader>;
    $[44] = t12;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== handleHoursClockPickerSelected || $[48] !== internalHours || $[49] !== mode) {
    t17 = mode === "hours" ? <ClockPicker key="hoursClockPicker" value={internalHours ?? 0} tickConfiguration={hoursTickConfiguration} onValueSelected={handleHoursClockPickerSelected} className={styles.clockPicker} data-testid="TimeClockPickerModal.HoursClockPicker" /> : null;
    $[47] = handleHoursClockPickerSelected;
    $[48] = internalHours;
    $[49] = mode;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== handleMinutesClockPickerSelected || $[52] !== internalMinutes || $[53] !== mode) {
    t18 = mode === "minutes" ? <ClockPicker key="minutesClockPicker" value={internalMinutes ?? 0} tickConfiguration={minutesTickConfiguration} onValueSelected={handleMinutesClockPickerSelected} className={styles.clockPicker} data-testid="TimeClockPickerModal.MinutesClockPicker" /> : null;
    $[51] = handleMinutesClockPickerSelected;
    $[52] = internalMinutes;
    $[53] = mode;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== t17 || $[56] !== t18) {
    t19 = <ConfirmModalBody>{t17}{t18}</ConfirmModalBody>;
    $[55] = t17;
    $[56] = t18;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== handleCancelClick || $[59] !== handleNextClick || $[60] !== mode || $[61] !== t) {
    t20 = mode === "hours" ? <><ButtonStandard onClick={handleCancelClick} data-testid="TimeClockPickerModal.CancelButton">{t("dateTime.TimeClockPickerModal.cancel")}</ButtonStandard><ButtonStandard type={ButtonStandardType.Primary} onClick={handleNextClick} data-testid="TimeClockPickerModal.NextButton">{t("dateTime.TimeClockPickerModal.next")}</ButtonStandard></> : null;
    $[58] = handleCancelClick;
    $[59] = handleNextClick;
    $[60] = mode;
    $[61] = t;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== handleFinishClick || $[64] !== handlePreviousClick || $[65] !== mode || $[66] !== t) {
    t21 = mode === "minutes" ? <><ButtonStandard onClick={handlePreviousClick} data-testid="TimeClockPickerModal.PreviousButton">{t("dateTime.TimeClockPickerModal.previous")}</ButtonStandard><ButtonStandard type={ButtonStandardType.Primary} onClick={handleFinishClick} data-testid="TimeClockPickerModal.FinishButton">{t("dateTime.TimeClockPickerModal.finish")}</ButtonStandard></> : null;
    $[63] = handleFinishClick;
    $[64] = handlePreviousClick;
    $[65] = mode;
    $[66] = t;
    $[67] = t21;
  } else {
    t21 = $[67];
  }
  let t22;
  if ($[68] !== t20 || $[69] !== t21) {
    t22 = <ConfirmModalButtonFooter buttonAlignment={ConfirmModalButtonFooterButtonAlignment.SpaceBetween}>{t20}{t21}</ConfirmModalButtonFooter>;
    $[68] = t20;
    $[69] = t21;
    $[70] = t22;
  } else {
    t22 = $[70];
  }
  let t23;
  if ($[71] !== opened || $[72] !== remainingProps || $[73] !== t16 || $[74] !== t19 || $[75] !== t22) {
    t23 = <ConfirmModal {...remainingProps} widthType={ConfirmModalWidthType.Auto} opened={opened}>{t16}{t19}{t22}</ConfirmModal>;
    $[71] = opened;
    $[72] = remainingProps;
    $[73] = t16;
    $[74] = t19;
    $[75] = t22;
    $[76] = t23;
  } else {
    t23 = $[76];
  }
  return t23;
});
const hoursTickConfiguration: ClockPickerProps['tickConfiguration'] = {
  outerTicks: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  innerTicks: [12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23]
};
const minutesTickConfiguration: ClockPickerProps['tickConfiguration'] = {
  outerTicks: [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55],
  innerTicks: []
};